import { useEffect, useMemo, useState } from 'react';
import { createApi } from './api.js';
import TicketList from './components/TicketList.jsx';
import TicketThread from './components/TicketThread.jsx';
import NewTicketForm from './components/NewTicketForm.jsx';

function useQueryParams() {
  return useMemo(() => new URLSearchParams(window.location.search), []);
}

export default function App() {
  const params = useQueryParams();
  const token = params.get('token');
  const productSlug = params.get('product');

  const api = useMemo(() => (token && productSlug ? createApi(token, productSlug) : null), [token, productSlug]);

  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [view, setView] = useState('list'); // 'list' | 'new' | ticketId
  const [customer, setCustomer] = useState(null);

  useEffect(() => {
    if (!api) {
      setLoading(false);
      setError('missing_auth');
      return;
    }
    api
      .get('/tickets')
      .then(({ data }) => {
        setTickets(data.tickets);
        setCustomer(data.customer);
      })
      .catch(() => setError('load_failed'))
      .finally(() => setLoading(false));
  }, [api]);

  function refreshTickets() {
    if (!api) return;
    api.get('/tickets').then(({ data }) => setTickets(data.tickets));
  }

  if (error === 'missing_auth') {
    return (
      <div className="h-screen flex items-center justify-center p-6 text-center text-sm text-gray-500">
        This support widget needs to be opened from within a product you're signed into.
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-white">
      <header className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
        <div>
          <h1 className="font-semibold text-gray-900">Sharre Support</h1>
          {customer && <p className="text-xs text-gray-500">{customer.email}</p>}
        </div>
        {view !== 'list' && (
          <button className="text-sm text-grey-700 hover:underline" onClick={() => setView('list')}>
            Back
          </button>
        )}
      </header>

      <main className="flex-1 overflow-y-auto">
        {loading && <div className="p-4 text-sm text-gray-500">Loading...</div>}

        {!loading && view === 'list' && (
          <TicketList tickets={tickets} onOpen={(id) => setView(id)} onNew={() => setView('new')} />
        )}

        {!loading && view === 'new' && (
          <NewTicketForm
            api={api}
            onCreated={(ticket) => {
              refreshTickets();
              setView(ticket.id);
            }}
          />
        )}

        {!loading && view !== 'list' && view !== 'new' && (
          <TicketThread api={api} ticketId={view} onSent={refreshTickets} />
        )}
      </main>
    </div>
  );
}
